import { trackIdentityQueueFetched } from '@/composables/tracking/useConversionTracking';
import useApiHelpers from './helpers';

const { getRequest } = useApiHelpers();

const REPOLL_DELAY_MS = 250;
const MAX_REPOLL_ATTEMPTS = 3;
// Wall-clock ceiling. Belt-and-braces against pathological BE behaviour or a stuck
// network call (axios has no per-request timeout configured — see `http.js`). Mirrors
// the cap used by the sibling post-payment poll in `kycQueuePersonalDetailsPollCore.js`.
const REPOLL_MAX_TOTAL_MS = 12_000;

const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Module-scoped so the timeline survives across multiple identity step transitions
// within the same checkout flow. Reset on full page reload, which aligns with a new flow.
let queueFetchIndex = 0;

const fetchKycQueueWithLogging = async () => {
  const response = await getRequest('/kyc/queue');
  queueFetchIndex += 1;
  trackIdentityQueueFetched({
    queue_fetch_index: queueFetchIndex,
    source: 'identity_flow',
    queue: response?.data,
  });
  return response;
};

/**
 * Recursive retry. Bounded by both an attempt counter AND a wall-clock cap, so a
 * misbehaving BE that keeps returning `repoll: true` (or a slow network) can't stall
 * the identity flow indefinitely. Matches the polling style in
 * `kycQueuePersonalDetailsPollCore.js` and avoids `no-await-in-loop`, which doesn't
 * apply since each retry is intentionally sequential.
 */
const fetchKycQueueWithRepoll = async (attempt = 0, startedAt = Date.now()) => {
  const response = await fetchKycQueueWithLogging();
  if (!response?.data?.rel?.repoll) return response;
  if (attempt >= MAX_REPOLL_ATTEMPTS) return response;
  if (Date.now() - startedAt >= REPOLL_MAX_TOTAL_MS) return response;
  await delay(REPOLL_DELAY_MS);
  return fetchKycQueueWithRepoll(attempt + 1, startedAt);
};

export default function useIdentityQueueEndpoints() {
  const getPendingIdentityDocuments = async () => fetchKycQueueWithRepoll();

  const getTinCountries = async () => {
    try {
      const response = await getRequest('/kyc/tin-types');
      const tinTypes = response?.data?.tinTypes || {};
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

      return Object.entries(tinTypes).map(([code, data]) => ({
        code,
        name: regionNames.of(code),
        ...data,
      }));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch TIN types', e);
      return [];
    }
  };

  return {
    getPendingIdentityDocuments,
    getTinCountries,
  };
}
